const partitionArray = <T>(array: T[], predicate: (element: T) => 0 | 1): [T[], T[]] =>
  array.reduce(
    (partition: [T[], T[]], element) => {
      partition[predicate(element)].push(element);
      return partition;
    },
    [[], []]
  );

const sort = <T extends object>(
  list: T[],
  getValueFunc: (item: T) => any,
  nullValueAtFirst = true
): T[] =>
  [...list].sort((itemA, itemB) => {
    const valueA = getValueFunc(itemA);
    const valueB = getValueFunc(itemB);

    if (!nullValueAtFirst) {
      if (valueA === null && valueB === null) {
        return 0;
      }
      if (valueA === null) {
        return 1;
      }
      if (valueB === null) {
        return -1;
      }
    }

    if (valueA == null) {
      return -1;
    }
    if (valueB == null) {
      return 1;
    }
    if (valueA > valueB) {
      return 1;
    }
    if (valueA < valueB) {
      return -1;
    }
    return 0;
  });

const sortAsModel = <T, M>(list: T[], model: M[], getModel: (item: T) => M): T[] =>
  [...list].sort((itemA, itemB) => model.indexOf(getModel(itemA)) - model.indexOf(getModel(itemB)));

const getLinesFromArrays = <T>(array1: T[], array2: T[]): [T | null, T | null][] =>
  new Array(Math.max(array1.length, array2.length))
    .fill({})
    .map((_, index) => [array1[index] ?? null, array2[index] ?? null]);

const findIndexOrNull = <T>(array: T[], predicate: (value: T) => boolean) => {
  const index = array.findIndex(predicate);
  if (index > -1) {
    return index;
  }

  return null;
};

const replaceOrInsert = <T, K extends keyof T>(array: T[], key: K, newValue: T): T[] => {
  const newList = [...array];
  const valueIndex = newList.findIndex(item => item[key] === newValue[key]);
  if (valueIndex >= 0) {
    newList.splice(valueIndex, 1, newValue);
  } else {
    newList.push(newValue);
  }
  return newList;
};

const groupBy = <T, K extends keyof T>(
  list: T[],
  key: K
): Record<T[K] extends string | number | symbol ? T[K] : string, T[]> =>
  list.reduce((acc, item) => {
    const keyValue = item[key] as T[K] extends string | number | symbol ? T[K] : string;
    if (keyValue) {
      if (!acc[keyValue]) {
        acc[keyValue] = [];
      }
      acc[keyValue].push(item);
    }
    return acc;
  }, {} as Record<T[K] extends string | number | symbol ? T[K] : string, T[]>);

const groupByPredicate = <T, K extends string>(
  list: T[],
  predicate: (item: T) => K
): Record<K, T[] | undefined> =>
  list.reduce((acc, item) => {
    const key = predicate(item);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {} as Record<K, T[] | undefined>);

const uniques = <T extends string | number>(array: T[]): T[] => Array.from(new Set(array));

const uniquesFromProp = <T extends object>(array: T[], keyToCompare: keyof T): T[] =>
  array.filter(
    (item, index, array) =>
      array.findIndex(searchedItem => searchedItem[keyToCompare] === item[keyToCompare]) === index
  );

export {
  partitionArray,
  sort,
  getLinesFromArrays,
  sortAsModel,
  findIndexOrNull,
  replaceOrInsert,
  groupBy,
  groupByPredicate,
  uniques,
  uniquesFromProp
};
